import React from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { FaUmbrellaBeach, FaRoute, FaHotel, FaUtensils, FaPlane, FaHeadset, FaCalendarAlt, FaMapMarkedAlt } from 'react-icons/fa';
import GlobeBanner from '../GlobeBanner';
import { keralaFestivals } from '../../data/keralaFestivals';
import { packagesList } from '../../data/packages';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './HomePage.css';

const HomePage = () => {
  // Sample testimonials data
  const testimonials = [
    {
      id: 1,
      name: 'Emma Thompson',
      country: 'United Kingdom',
      text: 'Our Kerala tour was absolutely magical! The backwaters of Alleppey, the tea plantations of Munnar, and the beautiful beaches - everything was perfectly arranged by Holistic Holidays LLP. The guides were knowledgeable and hospitable.',
      image: 'https://randomuser.me/api/portraits/women/32.jpg'
    },
    {
      id: 3,
      name: 'Sarah Johnson',
      country: 'Australia',
      text: 'From the moment we landed until our departure, everything was seamlessly arranged. The ayurvedic spa treatments, houseboat stay, and wildlife safari were highlights of our trip. Thank you Holistic Holidays LLP!',
      image: 'https://randomuser.me/api/portraits/women/45.jpg'
    },
    {
      id: 4,
      name: 'Rajesh Kumar',
      country: 'India',
      text: 'As an Indian traveler, I was amazed by how Holistic Holidays LLP showed me new perspectives of Kerala I never knew existed. Their attention to detail and local expertise made my trip truly special.',
      image: 'https://randomuser.me/api/portraits/men/22.jpg'
    }
  ];

  // Sample destinations data
  const destinations = [
    {
      id: 1,
      name: 'Alleppey Backwaters',
      image: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944',
      description: 'Experience the serene backwaters on a traditional houseboat.',
      link: '/destinations/alleppey'
    },
    {
      id: 2,
      name: 'Munnar Tea Gardens',
      image: 'https://images.unsplash.com/photo-1470115636492-6d2b56f9146d',
      description: 'Explore the lush green tea plantations and misty mountains.',
      link: '/destinations/munnar'
    },
    {
      id: 3,
      name: 'Wayanad Wildlife',
      image: '/images/Wayanad.jpg',
      description: 'Encounter diverse wildlife in the Western Ghats.',
      link: '/destinations/wayanad'
    }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <GlobeBanner />
        <div className="hero-content">
          <h1>Discover the Magic of Kerala & Beyond</h1>
          <p>Personalized travel experiences across India, with Kerala at heart</p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn btn-secondary">Plan Your Trip</Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section py-4">
        <div className="container">
          <div className="section-header">
            <h2>Welcome to Holistic Holidays LLP</h2>
            <p>Your gateway to authentic Indian experiences</p>
          </div>
          <div className="about-content">
            <div className="about-text">
              <p>
                At Holistic Holidays, We specialize in creating unforgettable travel experiences across India, with a special focus on Kerala for both domestic and international travelers. 
                Our deep local knowledge and personalized approach ensure you discover the true essence of India - from Kerala's serene backwaters 
                and pristine beaches to the diverse cultural heritage and mouthwatering cuisine across the country.
              </p>
              <Link to="/about" className="btn btn-primary">Learn More About Us</Link>
            </div>
            <div className="about-image">
              <img src="https://images.unsplash.com/photo-1593693411515-c20261bcad6e" alt="Kerala Culture" />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Destinations */}
      <section className="destinations-section py-4">
        <div className="container">
          <div className="section-header">
            <h2>Featured Destinations</h2>
            <p>Explore the most enchanting places in Kerala and beyond</p>
          </div>
          <div className="destinations-grid">
            {destinations.map(destination => (
              <div className="destination-card" key={destination.id}>
                <div className="destination-image">
                  <img src={destination.image} alt={destination.name} />
                </div>
                <div className="destination-info">
                  <h3>{destination.name}</h3>
                  <p>{destination.description}</p>
                  <Link to={destination.link} className="destination-link">Discover More</Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center my-3">
            <Link to="/destinations" className="btn btn-secondary">View All Destinations</Link>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="packages-section py-4">
        <div className="container">
          <div className="section-header">
            <h2>Our Packages</h2>
            <p>Customizable Alleppey experiences with homestay comfort and backwater adventures</p>
          </div>
          <div className="packages-grid">
            {packagesList.map(pkg => (
              <div className="package-card" key={pkg.id}>
                <div className="package-image">
                  <img src={pkg.image} alt={pkg.title} />
                  <div className="package-days">
                    <FaCalendarAlt />
                    <span>{pkg.days} Days</span>
                  </div>
                </div>
                <div className="package-content">
                  <h3>{pkg.title}</h3>
                  <ul className="package-highlights">
                    {pkg.highlights.map((highlight, index) => (
                      <li key={index}><FaMapMarkedAlt className="highlight-icon" /> {highlight}</li>
                    ))}
                  </ul>
                  <Link to={`/packages/${pkg.id}`} className="btn btn-primary">View Details</Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center my-3 package-cta-buttons">
            <Link to="/packages" className="btn btn-primary">View All Packages</Link>
            <Link to="/contact" className="btn btn-secondary">Custom Package Request</Link>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="events-section py-4">
        <div className="container">
          <div className="section-header">
            <h2>Kerala Festival Calendar</h2>
            <p>
              Kerala is known for its temple festivals, boat races, cultural celebrations, and religious festivals.
              These events attract large numbers of domestic and international visitors — perfect for planning your
              trip with Holistic Holidays LLP. (Festivals are subject to change.)
            </p>
          </div>
          <div className="events-calendar">
            {Object.entries(keralaFestivals).map(([month, events]) => (
              <div className="month-column" key={month}>
                <div className="month-header">
                  <h3>{month}</h3>
                </div>
                <div className="month-events">
                  {events.map((event, index) => (
                    <div className="event-item" key={index}>
                      <div className="event-name">{event.name}</div>
                      <div className="event-location">
                        <FaMapMarkedAlt className="location-icon" />
                        {event.location}
                      </div>
                      {event.highlights && (
                        <ul className="event-highlights">
                          {event.highlights.map((highlight, i) => (
                            <li key={i}>{highlight}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="text-center my-3">
            <Link to="/contact" className="btn btn-primary">Plan Your Festival Tour</Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section py-4" id="services">
        <div className="container">
          <div className="section-header">
            <h2>Our Services</h2>
            <p>Comprehensive travel services for a hassle-free experience</p>
          </div>
          <div className="services-grid">
            <div className="service-card">
              <FaRoute className="service-icon" />
              <h3>Customized Tours</h3>
              <p>Personalized itineraries for Kerala and pan-India experiences.</p>
            </div>
            <div className="service-card">
              <FaHotel className="service-icon" />
              <h3>Accommodation</h3>
              <p>Handpicked hotels, resorts, and authentic homestays across India.</p>
            </div>
            <div className="service-card">
              <FaUmbrellaBeach className="service-icon" />
              <h3>Activities</h3>
              <p>Exciting experiences from wildlife safaris to cultural shows.</p>
            </div>
            <div className="service-card">
              <FaUtensils className="service-icon" />
              <h3>Culinary Tours</h3>
              <p>Authentic cuisine experiences and cooking classes.</p>
            </div>
            <div className="service-card">
              <FaPlane className="service-icon" />
              <h3>Transport</h3>
              <p>Comfortable and reliable transportation throughout your journey.</p>
            </div>
            <div className="service-card">
              <FaHeadset className="service-icon" />
              <h3>24/7 Support</h3>
              <p>Round-the-clock assistance during your entire trip.</p>
            </div>
          </div>
          <div className="text-center my-3">
            <Link to="/contact" className="btn btn-primary">Contact Us for Services</Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section py-4">
        <div className="container">
          <div className="section-header">
            <h2>Traveler Stories</h2>
            <p>What our guests say about their experiences</p>
          </div>
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 5000 }}
            breakpoints={{
              640: {
                slidesPerView: 1,
              },
              768: {
                slidesPerView: 2,
              },
              1024: {
                slidesPerView: 3,
              },
            }}
            className="testimonials-slider"
          >
            {testimonials.map(testimonial => (
              <SwiperSlide key={testimonial.id} style={{ height: 'auto' }}>
                <div className="testimonial-card" style={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  padding: '20px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div className="testimonial-content" style={{ 
                    flex: 1,
                    marginBottom: '15px'
                  }}>
                    <p style={{ margin: 0 }}>"{testimonial.text}"</p>
                  </div>
                  <div className="testimonial-author" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <img src={testimonial.image} alt={testimonial.name} />
                    <div>
                      <h4>{testimonial.name}</h4>
                      <p>{testimonial.country}</p>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Experience the Magic of India?</h2>
            <p>Contact us today and let's start planning your dream vacation!</p>
            <Link to="/contact" className="btn btn-secondary">Get in Touch</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 